<script setup>
import { ref, onMounted, computed, watch, h } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { subDays, subWeeks, subMonths, subYears, format, isAfter, isPast } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Componentes
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ReportHeader from 'dashboard/routes/dashboard/settings/reports/components/ReportHeader.vue';
import Table from 'dashboard/components/table/Table.vue';
import ConversationPopUp from 'dashboard/components/ConversationPopUp.vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import axios from 'axios';

// Componentes Personalizados
import RelatorioFiltros from '../components/RelatorioFiltros.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();

// Estados
const isLoading = ref(true);
const dateRange = ref([]);
const selectedInbox = ref({ id: 'all', name: 'Todos os Canais' });
const selectedPeriod = ref({ id: 'day', name: 'Diário' });
const isCustomDateRange = ref(false);
const activeTab = ref('objections');

// Dados reais da API
const apiData = ref(null);

// Estado do modal de conversas
const showConversationModal = ref(false);
const modalConversations = ref([]);
const modalTitle = ref('');
const isLoadingModalData = ref(false);


// Auth
const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

const getAuthHeaders = () => {
  const cookie = getCookie('cw_d_session_info');
  if (!cookie) return {};
  try {
    const sessionInfo = JSON.parse(cookie);
    return {
      'access-token': sessionInfo['access-token'],
      client: sessionInfo['client'],
      uid: sessionInfo['uid']
    };
  } catch (e) {
    console.error('Erro ao ler o cookie:', e);
    return {};
  }
};

// Opções de agrupamento
const periodOptions = [
  { id: 'day', name: 'Diário' },
  { id: 'week', name: 'Semanal' },
  { id: 'month', name: 'Mensal' },
  { id: 'year', name: 'Anual' },
  { id: 'custom', name: 'Personalizado' }
];

// Carregar lista de inboxes
const inboxes = useMapGetter('inboxes/getInboxes');
const getInboxDisplayName = useMapGetter('inboxMetadata/getInboxDisplayName');
const inboxOptions = computed(() => {
  return [
    { id: 'all', name: 'Todos os Canais' },
    ...(inboxes.value || []).map(inbox => ({
      id: inbox.id,
      name: getInboxDisplayName.value(inbox.id) || inbox.name || 'Desconhecido'
    }))
  ];
});

// Ajustar dateRange com base no período selecionado
const updateDateRangeBasedOnPeriod = () => {
  if (isCustomDateRange.value) return;
  
  const endDate = new Date();
  let startDate;
  
  switch (selectedPeriod.value.id) {
    case 'day':
      startDate = subDays(endDate, 1);
      break;
    case 'week':
      startDate = subDays(endDate, 7);
      break;
    case 'month':
      startDate = subMonths(endDate, 1);
      break;
    case 'year':
      startDate = subYears(endDate, 1);
      break;
    default:
      startDate = subDays(endDate, 7);
  }
  
  dateRange.value = [startDate, endDate];
};

const fetchData = async () => {
  // Esta função agora é apenas um placeholder
};

// Dados do relatório - inicializa com estrutura vazia
const reportData = ref({
  totalConversations: 0,
  lists: {
    objections: [],
    compliments: [],
    complaints: [],
    customerRequests: [],
    negativeKeywords: []
  }
});

// Configurações para as tabelas
const createTableConfig = (data) => {
  const columnHelper = createColumnHelper();
  
  const columns = [
    columnHelper.accessor('text', {
      header: 'Descrição',
      cell: info => {
        const value = info.getValue();
        const row = info.row.original;
        return h('button', {
          class: 'text-left w-full hover:text-n-brand transition-colors duration-200 cursor-pointer',
          onClick: () => handleRowClick(row)
        }, value);
      },
    }),
    columnHelper.accessor('count', {
      header: 'Frequência',
      cell: info => info.getValue(),
    }),
  ];
  
  return useVueTable({
    get data() {
      return data;
    },
    columns,
    getCoreRowModel: getCoreRowModel(),
  });
};

// Configurar tabelas
const objectionsTable = computed(() => 
  createTableConfig(reportData.value.lists.objections)
);

const complimentsTable = computed(() => 
  createTableConfig(reportData.value.lists.compliments)
);

const complaintsTable = computed(() => 
  createTableConfig(reportData.value.lists.complaints)
);

const customerRequestsTable = computed(() => 
  createTableConfig(reportData.value.lists.customerRequests)
);

const negativeKeywordsTable = computed(() => 
  createTableConfig(reportData.value.lists.negativeKeywords)
);

// Handler para alterações de filtro
const handleFilterChange = (filters) => {
  if (filters.isCustomDate !== undefined) {
    isCustomDateRange.value = filters.isCustomDate;
  }
  
  if (filters.dateRange && filters.dateRange.length === 2) {
    dateRange.value = filters.dateRange;
    if (filters.isCustomDate) {
      selectedPeriod.value = { id: 'custom', name: 'Personalizado' };
    }
  }
  
  if (filters.period && filters.period.id) {
    selectedPeriod.value = filters.period;
  }
  
  if (filters.inbox && filters.inbox.id) {
    selectedInbox.value = filters.inbox;
  }
};

// Handler para quando os dados são carregados da API
const handleDataLoaded = (data) => {
  isLoading.value = false;
  apiData.value = data;
  
  if (data) {
    reportData.value = {
      totalConversations: data.total_conversations || 0,
      lists: {
        objections: data.lists?.objections || [],
        compliments: data.lists?.compliments || [],
        complaints: data.lists?.complaints || [],
        customerRequests: data.lists?.customer_requests || [],
        negativeKeywords: data.lists?.negative_keywords || []
      }
    };
  }
};

// Inicializar dados ao montar o componente
onMounted(() => {
  store.dispatch('inboxes/get');
});

// Navegação entre páginas
const navegarParaMetricas = () => {
  router.push({ name: 'ouroboros_metrics' });
};

const navegarParaGraficos = () => {
  router.push({ name: 'ouroboros_graph' });
};

const navegarParaConversas = () => {
  router.push({ name: 'home' });
};

const navegarParaAgentis = () => {
  router.push({ name: 'agentis_list' });
};

// Função para exportar os dados
const exportData = () => {
  alert('Exportando dados do relatório...');
};

// Função para lidar com o clique na linha
const handleRowClick = async (row) => {
  const listType = activeTab.value;
  const itemText = row.text;
  
  modalTitle.value = `Conversas - ${itemText}`;
  modalConversations.value = [];
  showConversationModal.value = true;
  isLoadingModalData.value = true;
  
  try {
    const headers = getAuthHeaders();
    
    const params = {
      account_id: store.getters['getCurrentAccountId'],
      list_type: listType,
      item_text: itemText
    };
    
    // Add date range if available
    if (dateRange.value && dateRange.value.length === 2) {
      params.start_date = format(dateRange.value[0], 'yyyy-MM-dd');
      params.end_date = format(dateRange.value[1], 'yyyy-MM-dd');
    }
    
    // Add inbox filter if not 'all'
    if (selectedInbox.value && selectedInbox.value.id !== 'all') {
      params.inbox_id = selectedInbox.value.id;
    }
    
    const response = await axios.get('/api/v1/ouroboros_dashboard/conversations_by_list', {
      params,
      headers
    });
    
    modalConversations.value = response.data.conversations || [];
    const totalCount = response.data.total_count || 0;
    modalTitle.value = `Conversas - ${itemText} (${totalCount})`;
  } catch (error) {
    console.error('Erro ao buscar conversas:', error);
    modalConversations.value = [];
    modalTitle.value = `Erro ao carregar conversas`;
  } finally {
    isLoadingModalData.value = false;
  }
};

// Função para fechar o modal
const closeConversationModal = () => {
  showConversationModal.value = false;
  modalConversations.value = [];
  modalTitle.value = '';
  isLoadingModalData.value = false;
};

// Configuração das tabs com informações detalhadas
const tabConfig = {
  objections: {
    title: 'Objeções Identificadas',
    description: 'Resistências e questionamentos apresentados pelos clientes que podem impedir o progresso da venda.',
    icon: 'i-lucide-alert-circle',
    color: 'amber',
    usage: 'Utilize estes dados para melhorar argumentações de vendas e antecipar objeções comuns.',
    emptyMessage: 'Nenhuma objeção identificada no período selecionado'
  },
  compliments: {
    title: 'Elogios e Feedback Positivo',
    description: 'Comentários positivos dos clientes sobre produtos, serviços ou qualidade do atendimento.',
    icon: 'i-lucide-award',
    color: 'green',
    usage: 'Use estes insights para reforçar pontos fortes e replicar boas práticas.',
    emptyMessage: 'Nenhum elogio registrado no período selecionado'
  },
  complaints: {
    title: 'Reclamações e Pontos de Melhoria',
    description: 'Insatisfações e problemas relatados pelos clientes que requerem atenção e correção.',
    icon: 'i-lucide-alert-triangle',
    color: 'red',
    usage: 'Priorize estes pontos para implementar melhorias e aumentar a satisfação.',
    emptyMessage: 'Nenhuma reclamação identificada no período selecionado'
  },
  requests: {
    title: 'Demandas e Necessidades',
    description: 'Pedidos, necessidades e requisitos mais frequentes apresentados pelos clientes.',
    icon: 'i-lucide-list-todo',
    color: 'blue',
    usage: 'Identifique oportunidades de produtos/serviços e melhore o atendimento às necessidades.',
    emptyMessage: 'Nenhuma demanda específica encontrada no período selecionado'
  },
  keywords: {
    title: 'Palavras-Chave Negativas',
    description: 'Palavrões, termos ofensivos ou palavras negativas identificadas nas conversas dos clientes.',
    icon: 'i-lucide-tag',
    color: 'purple',
    usage: 'Monitore estas palavras para identificar rapidamente situações de insatisfação, agressividade ou risco.',
    emptyMessage: 'Nenhuma palavra-chave negativa detectada no período selecionado'
  }
};

// Computado para configuração da tab ativa
const activeTabConfig = computed(() => {
  return tabConfig[activeTab.value] || tabConfig.objections;
});

// Computado para dados da tabela ativa
const activeTableData = computed(() => {
  switch (activeTab.value) {
    case 'objections':
      return reportData.value.lists.objections;
    case 'compliments':
      return reportData.value.lists.compliments;
    case 'complaints':
      return reportData.value.lists.complaints;
    case 'requests':
      return reportData.value.lists.customerRequests;
    case 'keywords':
      return reportData.value.lists.negativeKeywords;
    default:
      return [];
  }
});

// Computado para tabela ativa
const activeTable = computed(() => {
  switch (activeTab.value) {
    case 'objections':
      return objectionsTable.value;
    case 'compliments':
      return complimentsTable.value;
    case 'complaints':
      return complaintsTable.value;
    case 'requests':
      return customerRequestsTable.value;
    case 'keywords':
      return negativeKeywordsTable.value;
    default:
      return objectionsTable.value;
  }
});
</script>

<template>
  <div class="reports--wrapper overflow-auto bg-n-background w-full px-8 xl:px-0">
    <div class="max-w-[1200px] mx-auto pb-12">
      <!-- Header e Filtros -->
      <ReportHeader header-title="Análise Detalhada de Conteúdo das Conversas">
        <div class="flex space-x-2">
          <!--
          <Button
            label="Exportar Dados"
            icon="i-lucide-download"
            size="sm"
            @click="exportData"
          />
          -->
        </div>
      </ReportHeader>
      
      <!-- Descrição informativa -->
      <div class="mb-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
        <div class="flex items-start">
          <span class="i-lucide-search text-indigo-600 mr-2 mt-0.5 flex-shrink-0"></span>
          <div>
            <p class="text-sm text-indigo-800 font-medium mb-1">
              Análise de Conteúdo e Insights das Conversas
            </p>
            <p class="text-sm text-indigo-700">
              Explore padrões identificados nas conversas: objeções comuns, feedback dos clientes, 
              reclamações prioritárias e demandas específicas para otimizar estratégias de atendimento e vendas.
            </p>
          </div>
        </div>
      </div>
      
      <!-- Filtros -->
      <RelatorioFiltros
        :inbox-options="inboxOptions"
        :period-options="periodOptions"
        v-model:selected-inbox="selectedInbox"
        v-model:selected-period="selectedPeriod"
        v-model:date-range="dateRange"
        :auth-headers="getAuthHeaders()"
        :is-custom-date-range="isCustomDateRange"
        @filter-change="handleFilterChange"
        @data-loaded="handleDataLoaded"
      />

      
      <!-- Loading Estado -->
      <div v-if="isLoading" class="flex justify-center items-center py-16">
        <Spinner />
      </div>
      
      <div v-else>
        <!-- Aviso de sem dados com CTA -->
        <div v-if="reportData.totalConversations === 0" class="mt-6 mb-8">
          <div class="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
            <div class="flex flex-col md:flex-row items-center gap-6">
              <div class="flex-shrink-0">
                <span class="i-lucide-rocket text-5xl text-blue-600"></span>
              </div>
              <div class="flex-1 text-center md:text-left">
                <h3 class="text-lg font-semibold text-blue-900 mb-2">Descubra insights valiosos nas conversas!</h3>
                <p class="text-blue-800 mb-3">
                  Ainda não há dados para análise no período selecionado. Quando as conversas começarem, você poderá identificar padrões importantes como objeções, elogios e demandas dos clientes.
                </p>
                <p class="text-sm text-blue-700 mb-4">
                  Veja abaixo todos os tipos de análises que estarão disponíveis para otimizar suas estratégias de atendimento e vendas.
                </p>
                <div class="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                  <Button
                    label="Iniciar primeira conversa"
                    icon="i-lucide-message-circle"
                    color="blue"
                    size="sm"
                    @click="navegarParaConversas"
                  />
                  <Button
                    label="Configurar agentes"
                    icon="i-lucide-bot"
                    color="blue"
                    variant="smooth"
                    size="sm"
                    @click="navegarParaAgentis"
                  />
                </div>
              </div>
            </div>
            <div class="mt-4 pt-4 border-t border-blue-200">
              <p class="text-xs text-blue-700 text-center">
                <span class="i-lucide-info mr-1"></span>
                Período selecionado: {{ dateRange[0] ? format(dateRange[0], 'dd/MM/yyyy', { locale: ptBR }) : '' }} - {{ dateRange[1] ? format(dateRange[1], 'dd/MM/yyyy', { locale: ptBR }) : '' }} | Canal: {{ selectedInbox.name }}
              </p>
            </div>
          </div>
        </div>
        <!-- Tabs de navegação -->
        <div class="mb-6">
          <div class="bg-n-solid-1 rounded-lg p-1 border border-n-weak inline-flex gap-1">
          <button 
            class="px-3 py-1.5 text-sm rounded-md flex items-center"
            :class="activeTab === 'objections' ? 'bg-n-brand text-white' : 'text-n-slate-11 hover:text-n-slate-12'"
            @click="activeTab = 'objections'"
          >
            <span class="i-lucide-alert-circle mr-1.5 text-amber-500"></span>
            Objeções
          </button>
          <button 
            class="px-3 py-1.5 text-sm rounded-md flex items-center"
            :class="activeTab === 'compliments' ? 'bg-n-brand text-white' : 'text-n-slate-11 hover:text-n-slate-12'"
            @click="activeTab = 'compliments'"
          >
            <span class="i-lucide-award mr-1.5 text-green-500"></span>
            Elogios
          </button>
          <button 
            class="px-3 py-1.5 text-sm rounded-md flex items-center"
            :class="activeTab === 'complaints' ? 'bg-n-brand text-white' : 'text-n-slate-11 hover:text-n-slate-12'"
            @click="activeTab = 'complaints'"
          >
            <span class="i-lucide-alert-triangle mr-1.5 text-red-500"></span>
            Reclamações
          </button>
          <button 
            class="px-3 py-1.5 text-sm rounded-md flex items-center"
            :class="activeTab === 'requests' ? 'bg-n-brand text-white' : 'text-n-slate-11 hover:text-n-slate-12'"
            @click="activeTab = 'requests'"
          >
            <span class="i-lucide-list-todo mr-1.5 text-blue-500"></span>
            Demandas
          </button>
          <button 
            class="px-3 py-1.5 text-sm rounded-md flex items-center"
            :class="activeTab === 'keywords' ? 'bg-n-brand text-white' : 'text-n-slate-11 hover:text-n-slate-12'"
            @click="activeTab = 'keywords'"
          >
            <span class="i-lucide-tag mr-1.5 text-purple-500"></span>
            Palavras-Chave Negativas
          </button>
        </div>
          
        </div>
        
        <!-- Conteúdo da tabela -->
        <div class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 p-4">
          <div class="flex items-start justify-between mb-4">
            <div class="flex-1">
              <div class="flex items-center mb-2">
                <span :class="[activeTabConfig.icon, `text-${activeTabConfig.color}-500`]" class="mr-2"></span>
                <h3 class="text-lg font-medium">{{ activeTabConfig.title }}</h3>
              </div>
              <p class="text-sm text-n-slate-11 mb-2">
                {{ activeTabConfig.description }}
              </p>
              <div class="flex items-center text-xs text-n-slate-10">
                <span class="i-lucide-database mr-1"></span>
                {{ activeTableData.length }} {{ activeTableData.length === 1 ? 'item encontrado' : 'itens encontrados' }}
              </div>
            </div>
          </div>
          
          <div v-if="activeTableData.length === 0" class="text-center py-8 text-n-slate-11 flex flex-col items-center">
            <span class="i-lucide-inbox mr-2"></span>
            {{ reportData.totalConversations === 0 ? 'Sem dados no período selecionado' : activeTabConfig.emptyMessage }}
          </div>
          
          <Table v-else :table="activeTable" />
          
          <!-- Como utilizar -->
          <div class="mt-4 p-4 bg-n-alpha-3 rounded-lg border border-n-weak">
            <p class="text-sm font-medium text-n-slate-12 mb-2">Como utilizar:</p>
            <p class="text-sm text-n-slate-11">{{ activeTabConfig.usage }}</p>
          </div>
        </div>
        
        <!-- Seção de dicas de uso consolidadas -->
        <div class="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Dicas de análise -->
          <div class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 p-6">
            <h4 class="text-base font-semibold flex items-center text-green-600 mb-3">
              <span class="i-lucide-target mr-2"></span>
              Estratégias de Uso
            </h4>
            <ul class="text-sm space-y-2">
              <li class="flex items-start">
                <span class="i-lucide-check-circle mr-2 mt-0.5 flex-shrink-0 text-green-600"></span>
                <span><strong>Objeções:</strong> Desenvolva argumentos específicos para as objeções mais frequentes</span>
              </li>
              <li class="flex items-start">
                <span class="i-lucide-check-circle mr-2 mt-0.5 flex-shrink-0 text-green-600"></span>
                <span><strong>Elogios:</strong> Identifique pontos fortes para destacar em futuras apresentações</span>
              </li>
              <li class="flex items-start">
                <span class="i-lucide-check-circle mr-2 mt-0.5 flex-shrink-0 text-green-600"></span>
                <span><strong>Reclamações:</strong> Priorize melhorias baseadas na frequência dos problemas</span>
              </li>
              <li class="flex items-start">
                <span class="i-lucide-check-circle mr-2 mt-0.5 flex-shrink-0 text-green-600"></span>
                <span><strong>Demandas:</strong> Explore oportunidades de novos produtos/serviços</span>
              </li>
            </ul>
          </div>
          
          <!-- Próximos passos -->
          <div class="shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 p-6">
            <h4 class="text-base font-semibold text-amber-400 flex items-center mb-3">
              <span class="i-lucide-trending-up mr-2"></span>
              Próximos Passos Recomendados
            </h4>
            <ul class="text-sm space-y-2">
              <li class="flex items-start">
                <span class="i-lucide-arrow-right mr-2 mt-0.5 flex-shrink-0"></span>
                <span>Agende reuniões para discutir os insights identificados</span>
              </li>
              <li class="flex items-start">
                <span class="i-lucide-arrow-right mr-2 mt-0.5 flex-shrink-0"></span>
                <span>Crie planos de ação para abordar as principais reclamações</span>
              </li>
              <li class="flex items-start">
                <span class="i-lucide-arrow-right mr-2 mt-0.5 flex-shrink-0"></span>
                <span>Desenvolva treinamentos baseados nas objeções identificadas</span>
              </li>
              <li class="flex items-start">
                <span class="i-lucide-arrow-right mr-2 mt-0.5 flex-shrink-0"></span>
                <span>Monitore regularmente as tendências e mudanças nos padrões</span>
              </li>
            </ul>
          </div>
        </div>
        
        <!-- Funcionalidades futuras -->
        <div class="mt-8 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 p-6">
          <div class="flex items-start">
            <span class="i-lucide-clock mr-3 mt-0.5 flex-shrink-0"></span>
            <div>
                <h4 class="text-base font-semibold mb-2">
                Funcionalidades em Desenvolvimento
                </h4>
                <div class="text-sm space-y-1">
                <p>• <strong>Transferências Humanas:</strong> Padrões e motivos de escalação para atendimento humano</p>
                <p>• <strong>Análise Temporal:</strong> Evolução dos padrões ao longo do tempo</p>
                <p>• <strong>Verificação de Conversas:</strong> Possibilidade de acessar conversas onde ocorreram os casos identificados</p>
                </div>
              </div>
          </div>
        </div>
        
        <!-- Navegação para outras páginas -->
        <div class="mt-8 text-center">
          <p class="text-sm text-n-slate-11 mb-3">Explore análises mais detalhadas:</p>
          <div class="flex justify-center space-x-4">
            <Button
              label="Análise de Métricas"
              icon="i-lucide-list"
              color="blue"
              @click="navegarParaMetricas"
            />
          </div>
        </div>
      </div>
    </div>
    
    <!-- Modal de Conversas -->
    <ConversationPopUp
      v-if="showConversationModal"
      :conversations="modalConversations"
      :title="modalTitle"
      :loading="isLoadingModalData"
      :subtitle="`Mostrando ${modalConversations.length} ${modalConversations.length === 1 ? 'conversa' : 'conversas'} mais recentes`"
      @close="closeConversationModal"
    />
  </div>
</template>

<style scoped>
.reports--wrapper :deep(.multiselect__tags),
.reports--wrapper :deep(.multiselect__single),
.reports--wrapper :deep(.multiselect__input) {
  @apply bg-n-alpha-3 border-n-weak text-n-slate-12 rounded-lg text-sm min-h-[2.5rem];
}

.reports--wrapper :deep(.multiselect__placeholder) {
  @apply text-n-slate-11;
}

.reports--wrapper :deep(.mx-input-wrapper) {
  @apply bg-n-alpha-3 border-n-weak text-n-slate-12 rounded-lg text-sm;
}

.reports--wrapper :deep(.mx-input-wrapper input) {
  @apply text-n-slate-11;
}
</style>